import { FC, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Style } from '@capacitor/status-bar';
import { confirmPasswordReset, verifyPasswordResetCode } from 'firebase/auth';
import { getAuth } from 'firebase/auth';

import { SuccessStep } from '@mopla/constants';
import {
	ChangePasswordForm,
	Footer,
	MainWithFooterTemplate,
	useStatusBarStyleSync,
} from '@mopla/ui';
import moplaWelcomeLogo from '@mopla/ui/icons/moplaWelcomeLogo.svg?url';
import { ContentWrapper } from '@mopla/ui/layouts/WelcomeScreen';
import { usePersistentCountdownGuard } from '@mopla/utils';

import { useWelcomePageStoreContext } from '../../useWelcomePageStore';
import { TSetInterceptor } from '../../utils/useInterceptor';
import { MoplaLogoImgStyled } from '../styled';

export interface IChangePasswordFlowProps {
	setCloseInterceptor: TSetInterceptor;
	onBottomSliderClose: VoidFunction;
}

export const ChangePasswordFlow: FC<IChangePasswordFlowProps> = ({
	setCloseInterceptor,
	onBottomSliderClose,
}) => {
	const { resetCountdownPersistence } =
		usePersistentCountdownGuard('emailSendTime');
	const { successType, setSuccessType } = useWelcomePageStoreContext();
	const { push, pop } = useStatusBarStyleSync();
	const navigate = useNavigate();
	const showSuccess = successType === SuccessStep.passwordChanged;

	useEffect(() => {
		if (!showSuccess) {
			setCloseInterceptor(onBottomSliderClose);
		}

		return () => {
			setCloseInterceptor(null);
		};
	}, [onBottomSliderClose, setCloseInterceptor, showSuccess]);

	useEffect(() => {
		push({
			title: 'ForgotPasswordFlow',
			style: Style.Light,
		});

		return () => {
			pop();
		};
	}, [push, pop]);

	const onSuccessHandler = useCallback(() => {
		setSuccessType(SuccessStep.passwordChanged);

		/** On success, we reset the persistent countdown timer, which otherwise would prevent users from resending emails too frequently. */
		resetCountdownPersistence();
	}, [resetCountdownPersistence, setSuccessType]);

	return (
		<ChangePasswordForm
			onSuccessCallback={onSuccessHandler}
			verifyPasswordResetCode={verifyPasswordResetCode}
			confirmPasswordReset={confirmPasswordReset}
			getAuth={getAuth}
			showSuccess={successType === SuccessStep.passwordChanged}
			onSubmitCallback={() =>
				navigate({ pathname: '/welcome', search: 'login' })
			}
			onErrorCallback={() =>
				navigate({ pathname: '/welcome', search: 'login' })
			}
			render={({ content, submit }) => (
				<MainWithFooterTemplate
					className="mainWithFooter"
					content={
						<ContentWrapper>
							{!successType && (
								<MoplaLogoImgStyled
									className="logo"
									src={moplaWelcomeLogo}
									alt=""
								/>
							)}
							{content}
						</ContentWrapper>
					}
					footer={<Footer>{submit}</Footer>}
				/>
			)}
		/>
	);
};
